import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';

/** Normalize for compare: trim, drop leading #, lowercase. */
export function normalizeCanvasConfirmName(value) {
  return String(value || '')
    .trim()
    .replace(/^#+/, '')
    .toLowerCase();
}

export function canvasConfirmNameMatches(input, hashtag) {
  const expected = normalizeCanvasConfirmName(hashtag);
  if (!expected) return false;
  return normalizeCanvasConfirmName(input) === expected;
}

const DEFAULT_COPY = {
  title: 'Delete canvas permanently?',
  hint: name =>
    `Type #${name} to confirm. All pins and related data will be removed.`,
  label: 'Canvas name',
  cancel: 'Cancel',
  confirm: 'Delete forever',
  deleting: 'Deleting…',
};

/**
 * Type-the-name confirmation before permanently deleting a canvas.
 * Accepts either `japan` or `#japan`.
 */
export default function DeleteCanvasConfirmDialog({
  open,
  hashtag,
  onClose,
  onConfirm,
  busy = false,
  error = '',
  copy = DEFAULT_COPY,
}) {
  const [confirmName, setConfirmName] = useState('');
  const name = String(hashtag || '').trim();
  const matches = canvasConfirmNameMatches(confirmName, name);
  const title = copy.title || DEFAULT_COPY.title;
  const hint = (copy.hint || DEFAULT_COPY.hint)(name);
  const label = copy.label || DEFAULT_COPY.label;
  const cancelLabel = copy.cancel || DEFAULT_COPY.cancel;
  const confirmLabel = busy
    ? copy.deleting || DEFAULT_COPY.deleting
    : copy.confirm || DEFAULT_COPY.confirm;

  useEffect(() => {
    if (open) setConfirmName('');
  }, [open, name]);

  const handleConfirm = () => {
    if (!matches || busy) return;
    void onConfirm?.();
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {hint}
        </Typography>
        <TextField
          autoFocus
          fullWidth
          size="small"
          label={label}
          placeholder={name}
          value={confirmName}
          onChange={e => setConfirmName(e.target.value)}
          disabled={busy || !name}
          inputProps={{
            'aria-label': label,
            autoComplete: 'off',
            spellCheck: false,
          }}
          onKeyDown={e => {
            if (e.key === 'Enter' && matches) {
              e.preventDefault();
              handleConfirm();
            }
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {cancelLabel}
        </Button>
        <Button
          color="error"
          variant="contained"
          disabled={!matches || busy}
          onClick={handleConfirm}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
